/**
 * Backend client. The browser never talks to YAMCS directly (handover 4.1) - every request
 * goes through our API, which owns credentials, caching and resampling.
 */

import type {
  CoverageResponse,
  DiagnoseResponse,
  EventsResponse,
  HealthResponse,
  MdbStatusResponse,
  SeriesResponse,
  SpacecraftResponse,
  SpinTraceResponse,
  TimeWindow,
} from './types';

const BASE = import.meta.env.VITE_API_BASE ?? '/api/v1';

/**
 * Every request carries a deadline. Without one, a connection that dies mid-flight (a backend
 * deploy, a suspended Fly machine, a dropped network) leaves fetch pending forever and the UI
 * stuck on "Loading" with no error to act on. 90 s comfortably covers a cold cache fill.
 */
export const REQUEST_TIMEOUT_MS = 90_000;

export function requestSignal(): AbortSignal {
  return AbortSignal.timeout(REQUEST_TIMEOUT_MS);
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly detail?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function iso(date: Date): string {
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

function windowParams(win: TimeWindow): Record<string, string> {
  return { start: iso(win.start), stop: iso(win.stop) };
}

async function request<T>(path: string, params: Record<string, string | number | boolean> = {}): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(params)) {
    url.searchParams.set(key, String(value));
  }
  let response: Response;
  try {
    response = await fetch(url, {
      headers: { Accept: 'application/json' },
      signal: requestSignal(),
    });
  } catch (error) {
    const reason =
      error instanceof DOMException && error.name === 'TimeoutError'
        ? `no response within ${REQUEST_TIMEOUT_MS / 1000}s`
        : String(error instanceof Error ? error.message : error);
    throw new ApiError(`${path} unreachable (${reason})`, 0, reason);
  }
  if (!response.ok) {
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = body.detail ?? JSON.stringify(body);
    } catch {
      /* keep the status text */
    }
    throw new ApiError(`${path} failed (${response.status})`, response.status, detail);
  }
  return (await response.json()) as T;
}

export const api = {
  health: () => request<HealthResponse>('/health'),
  spacecraft: () => request<SpacecraftResponse>('/spacecraft'),
  mdbStatus: (refresh = false) => request<MdbStatusResponse>('/mdb/status', { refresh }),

  coverage: (sat: string, win: TimeWindow) =>
    request<CoverageResponse>(`/${sat}/coverage`, windowParams(win)),

  events: (sat: string, win: TimeWindow) =>
    request<EventsResponse>(`/${sat}/events`, windowParams(win)),

  series: (sat: string, dataset: string, win: TimeWindow, maxPoints = 5000) =>
    request<SeriesResponse>(`/${sat}/series/${dataset}`, {
      ...windowParams(win),
      max_points: maxPoints,
    }),

  spinTrace: (sat: string, win: TimeWindow, maxPoints = 3000) =>
    request<SpinTraceResponse>(`/${sat}/spin_trace`, {
      ...windowParams(win),
      max_points: maxPoints,
    }),

  diagnose: (sat: string, win: TimeWindow) =>
    request<DiagnoseResponse>(`/${sat}/attitude/diagnose`, windowParams(win)),

  /** CZML is loaded by Cesium's own data source, so this is the URL rather than the payload. */
  czmlUrl(sats: string[], win: TimeWindow, step: number, multiplier = 60): string {
    const url = new URL(`${BASE}/czml`, window.location.origin);
    url.searchParams.set('sats', sats.join(','));
    url.searchParams.set('start', iso(win.start));
    url.searchParams.set('stop', iso(win.stop));
    url.searchParams.set('step', String(step));
    url.searchParams.set('multiplier', String(multiplier));
    return url.toString();
  },

  liveUrl(sat: string): string {
    return new URL(`${BASE}/live/${sat}`, window.location.origin).toString();
  },
};

export { iso };
